import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection,
  SeverityLevel
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function Section41ExplainedGuide() {
  return (
    <GuideLayout
      slug="section-41-explained"
      category="Legal"
      categoryColor="success"
      title="Section 41 of the Highways Act Explained"
      description="The law that makes councils responsible for road maintenance — and the foundation of every successful pothole claim."
      readTime="6 min"
      updatedDate="12 January 2026"
      reviewedFor="UK law"
      sources={[
        { name: 'legislation.gov.uk', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'Section 58', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58' },
      ]}
      relatedGuides={[
        { slug: 'section-58-defence', title: 'The Section 58 Defence', readTime: '7 min' },
        { slug: 'prior-reports-explained', title: 'Why Prior Reports Matter', readTime: '5 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Claim?', readTime: '5 min' },
      ]}
      atAGlance={[
        { label: 'What Section 41 says', value: 'Highway authorities have a statutory duty to maintain highways maintainable at public expense (Highways Act 1980)' },
        { label: 'What the claimant must establish', value: 'The relevant highway, a dangerous defect, breach of the maintenance duty, causation and loss' },
        { label: 'Who it applies to', value: 'Local councils, National Highways, and Transport for London — not private or unadopted roads' },
        { label: "The authority's response", value: 'The authority may rely on Section 58 and evidence of a reasonable inspection and maintenance system' },
        { label: 'What may weaken the defence', value: 'Defect-specific reports may be relevant when verified against the timing, inspection regime and authority response' },
      ]}
      howTo={{
        name: 'How to Use Section 41 in Your Pothole Claim',
        description:
          'Five steps to build a pothole claim on the Section 41 duty to maintain and anticipate the council\'s Section 58 defence.',
        steps: [
          {
            name: "Establish the council's duty",
            text: 'Use the postcode result as a likely-authority lead, then verify that the road is publicly maintained and confirm the specific road manager through official records.',
          },
          {
            name: 'Document the breach',
            text: "Photograph the pothole showing it's a dangerous defect, not just normal wear. Include a measurement if possible to prove depth.",
          },
          {
            name: 'Link breach to damage',
            text: 'Show the defect caused the damage. Photos of the pothole, damage to the vehicle, and the repair invoice all work together.',
          },
          {
            name: 'Cite Section 41 in the claim',
            text: 'The claim letter should explicitly cite Section 41 of the Highways Act 1980 and state that the council breached their statutory duty.',
          },
          {
            name: 'Prepare for the Section 58 defence',
            text: "Anticipate the council's defence. Verified, defect-specific reports may support an argument about notice, depending on their timing, the inspection regime and the authority's response.",
          },
        ],
      }}
      faqs={[
        {
          question: 'What exactly does Section 41 of the Highways Act 1980 say?',
          answer: 'Section 41 states that highway authorities have a duty to maintain highways that are maintainable at public expense. In simple terms, if a council is responsible for a road, they have a legal obligation to keep it in a safe condition for road users.',
        },
        {
          question: 'Does Section 41 guarantee I\'ll win my pothole claim?',
          answer: 'No. While Section 41 creates the duty to maintain roads, Section 58 of the same Act provides councils with a defence. Prior reports may support an argument about notice, but their relevance depends on defect-specific evidence, timing, the inspection regime, the authority response, causation and loss.',
        },
        {
          question: 'Who does Section 41 apply to?',
          answer: 'Section 41 applies to all highway authorities in England and Wales. This includes local councils (responsible for most local roads), National Highways (motorways and major A-roads), and Transport for London (red routes in London). It does not apply to private roads or unadopted roads.',
        },
        {
          question: 'What counts as "failure to maintain" under Section 41?',
          answer: 'A road that poses a danger to users due to its condition is considered poorly maintained. This includes potholes (typically 40mm+ deep), sunken drains, raised ironwork, severe surface deterioration, and tree root damage. Minor imperfections don\'t constitute failure to maintain.',
        },
        {
          question: 'Do I need to prove the council was negligent?',
          answer: 'A Section 41 claim concerns the statutory maintenance duty rather than a free-standing allegation of negligence. The claimant still needs evidence about the relevant highway, defect, breach, causation and loss, and the authority may rely on Section 58. The outcome is fact-specific.',
        },
        {
          question: 'How does Section 41 interact with the Section 58 defence?',
          answer: 'Section 41 creates the duty and Section 58 provides a defence. The outcome depends on the evidence, including the inspection system and records, any defect-specific reports, their timing and the authority\'s response, together with causation and loss. A prior report does not determine the issue by itself.',
        },
        {
          question: 'Should my claim letter mention Section 41?',
          answer: 'Yes. Citing Section 41 of the Highways Act 1980 in your claim letter shows you understand the legal basis for your claim and signals that you\'re serious. It also puts the council on notice that you expect them to either accept liability or provide evidence for their Section 58 defence.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        }
        title="What is Section 41?"
      >
        <p className="mb-4">
          Section 41 of the Highways Act 1980 is the legal foundation of{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            every valid pothole claim
          </Link>
          . It creates 
          a <strong>statutory duty</strong> on highway authorities to maintain the roads they're responsible 
          for. This isn't optional guidance — it's the law.
        </p>
        <GuideTip variant="info" title="Why this matters:">
          When the council fails in this duty and you suffer damage as a result, you have legal 
          grounds to claim compensation. Section 41 is your starting point.
        </GuideTip>
      </GuideSection>

      <div className="bg-brand-950 rounded-2xl p-6 mb-8">
        <p className="text-brand-400 text-xs font-semibold tracking-wider uppercase mb-3">
          HIGHWAYS ACT 1980, SECTION 41(1)
        </p>
        <blockquote className="text-xl text-white italic leading-relaxed">
          "The authority who are for the time being the highway authority for a highway maintainable 
          at the public expense are under a duty... to maintain the highway."
        </blockquote>
        <p className="text-brand-300 mt-4 text-sm">
          That's it. Simple, clear, and powerful. If a council is responsible for a road, they 
          must maintain it.
        </p>
      </div>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="What This Means for Your Claim"
      >
        <p className="mb-4">
          Section 41 supplies the statutory duty, but a claimant still needs evidence for each
          part of the case:
        </p>
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Relevant Highway"
          >
            Verify that the road is maintainable at public expense and identify its specific road manager
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Dangerous Defect"
          >
            Show the condition and why it amounted to a failure to maintain in the circumstances
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Causation"
          >
            Link the particular defect to the incident and the reported vehicle damage
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Loss"
          >
            Support the amount claimed with appropriate repair or replacement evidence
          </GuideCard>
        </GuideGrid>
        <p className="mt-4 text-surface-700">
          The authority may answer that evidence with a Section 58 defence. Inspection records,
          defect-specific reports, timing and the authority&apos;s response may all be relevant.
        </p>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="What Counts as 'Failure to Maintain'?"
      >
        <p className="text-brand-200 mb-4">
          A road that's safe for normal use is considered properly maintained. Failure includes:
        </p>
        <GuideGrid cols={2}>
          <GuideCard variant="dark" title="Potholes">
            Holes in the road surface, typically 40mm+ deep
          </GuideCard>
          <GuideCard variant="dark" title="Sunken Drains">
            Manhole covers or drains dropped below road level
          </GuideCard>
          <GuideCard variant="dark" title="Raised Ironwork">
            Drain covers or chambers protruding above the surface
          </GuideCard>
          <GuideCard variant="dark" title="Surface Deterioration">
            Significant breaking up or crumbling of the road
          </GuideCard>
        </GuideGrid>
        <div className="bg-white/10 border border-white/20 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 text-white">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-white">
              <p className="font-semibold mb-1">Minor imperfections don't count</p>
              <p className="text-sm leading-relaxed text-white/90">
                Roads don't need to be perfectly smooth. Minor cracks, slight unevenness, and normal wear 
                aren't failures to maintain — but defects that pose a danger to road users cross the line.
              </p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        }
        title="The Council's Escape: Section 58"
      >
        <p className="mb-4">
          While Section 41 creates the duty,{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            Section 58 gives councils a defence
          </Link>
          . They can avoid 
          liability if they prove they had:
        </p>
        <div className="space-y-3 mb-4">
          <SeverityLevel level="high" badge="Key Defence">
            A reasonable system for inspecting roads regularly
          </SeverityLevel>
          <SeverityLevel level="medium" badge="Evidence Needed">
            Records showing they inspected the road according to that system
          </SeverityLevel>
          <SeverityLevel level="low" badge="Either/Or">
            The defect wasn't visible at the last inspection, OR they had reasonable repair timescales
          </SeverityLevel>
        </div>
        <GuideTip variant="success" title="How prior reports may help">
          A verified, <strong>defect-specific prior report</strong> may support an argument about
          notice or weaken a Section 58 defence. Its weight depends on timing, the inspection
          regime, the authority&apos;s response, causation and loss. Fixtyer surfaces research
          leads for you to verify against official records.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title="Who Does Section 41 Apply To?"
      >
        <p className="mb-4">
          Section 41 applies to all{' '}
          <Link to="/guides/who-is-responsible" className={inlineLink}>
            highway authorities
          </Link>
          {' '}— the bodies responsible for maintaining 
          public roads:
        </p>
        <GuideGrid cols={3}>
          <GuideCard title="Local Councils">
            Most local roads: A-roads, B-roads, residential streets, and unclassified roads
          </GuideCard>
          <GuideCard title="National Highways">
            Motorways and major trunk roads (the strategic road network)
          </GuideCard>
          <GuideCard title="Transport for London">
            Major roads in London (red routes), including the A1, A2, A40, etc.
          </GuideCard>
        </GuideGrid>
        <GuideTip variant="warning" title="Section 41 does NOT apply to:">
          Private roads, unadopted roads, private car parks, supermarket car parks, or any road 
          not maintained at public expense.
        </GuideTip>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Establish the Council's Duty">
          <p>
            Treat the postcode result as a likely-authority lead. Verify that the road is publicly
            maintained and confirm the specific road manager through the authority&apos;s or strategic
            road operator&apos;s official records.
          </p>
        </GuideStep>

        <GuideStep number={2} title="Document the Breach">
          <p>
            <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
              Photograph the pothole
            </Link>
            {' '}showing it's a dangerous defect — not just normal wear. Include 
            a measurement if possible to prove depth.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Link Breach to Damage">
          <p>
            Your evidence should show the defect caused your damage. Photos of the pothole, damage 
            to your vehicle, and the repair invoice all work together.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Cite Section 41 in Your Claim">
          <p className="mb-2">
            Your claim letter should explicitly cite Section 41 of the Highways Act 1980 and state 
            that the council breached their statutory duty.
          </p>
          <GuideTip variant="info">
            A Fixtyer pack can include a Section 41 reference for you to review. Check that it applies
            to the specific road and facts before relying on it.
          </GuideTip>
        </GuideStep>

        <GuideStep number={5} title="Prepare for Section 58 Defence">
          <p>
            Anticipate the council's defence.{' '}
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              Prior reports of the pothole
            </Link>
            {' '}may support an argument about notice or weaken the defence, depending on timing,
            the inspection regime, the authority&apos;s response, defect-specific evidence,
            causation and loss.
          </p>
        </GuideStep>
      </GuideSteps>
    </GuideLayout>
  );
}
